import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/Card";
import Button from "../components/Button";
import Sidebar from "../components/Sidebar";
import {
  BellIcon,
  LockIcon,
  UserIcon,
  SettingsIcon,
  LogoutIcon,
} from "../components/Icons";

const Settings = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
    trades: true,
    promotions: false,
  });

  const [security, setSecurity] = useState({
    twoFactor: true,
    biometric: false,
  });

  const toggleNotification = (key) => {
    setNotifications({ ...notifications, [key]: !notifications[key] });
  };

  const toggleSecurity = (key) => {
    setSecurity({ ...security, [key]: !security[key] });
  };

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 md:p-8 md:ml-64">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Settings
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            Manage your account preferences
          </p>
        </div>

        <div className="space-y-6">
          {/* Notifications */}
          <Card>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 card-gradient rounded-full flex items-center justify-center">
                <BellIcon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Notifications</h3>
            </div>
            <div className="space-y-4">
              <ToggleItem
                label="Email Notifications"
                description="Receive updates via email"
                checked={notifications.email}
                onChange={() => toggleNotification("email")}
              />
              <ToggleItem
                label="Push Notifications"
                description="Get real-time alerts on your device"
                checked={notifications.push}
                onChange={() => toggleNotification("push")}
              />
              {/* <ToggleItem
                label="SMS Notifications"
                description="Receive text messages for important updates"
                checked={notifications.sms}
                onChange={() => toggleNotification("sms")}
              /> */}
              <ToggleItem
                label="Trade Alerts"
                description="Get notified about your trades"
                checked={notifications.trades}
                onChange={() => toggleNotification("trades")}
              />
              {/* <ToggleItem
                label="Promotional Offers"
                description="Receive special offers and promotions"
                checked={notifications.promotions}
                onChange={() => toggleNotification("promotions")}
              /> */}
            </div>
          </Card>

          {/* Security */}
          <Card>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 card-gradient rounded-full flex items-center justify-center">
                <LockIcon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Security</h3>
            </div>
            <div className="space-y-4">
              <ToggleItem
                label="Two-Factor Authentication"
                description="Add an extra layer of security"
                checked={security.twoFactor}
                onChange={() => toggleSecurity("twoFactor")}
              />
              {/* <ToggleItem
                label="Biometric Login"
                description="Use fingerprint or face ID"
                checked={security.biometric}
                onChange={() => toggleSecurity("biometric")}
              /> */}
              <button className="w-full p-4 bg-surface-soft rounded-xl hover:bg-surface-hover transition-colors text-left flex items-center justify-between">
                <div>
                  <p className="text-[var(--text-primary)] font-medium">Change Password</p>
                  <p className="text-[var(--text-primary)] text-xs">
                    Update your password regularly
                  </p>
                </div>
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
            </div>
          </Card>

          {/* Account */}
          <Card>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 card-gradient rounded-full flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Account</h3>
            </div>
            <div className="space-y-3">
              <button className="w-full p-4 bg-surface-soft rounded-xl hover:bg-surface-hover transition-colors text-left flex items-center justify-between">
                <div>
                  <p className="text-[var(--text-primary)] font-medium">Personal Information</p>
                  <p className="text-[var(--text-primary)] text-xs">
                    Update your profile details
                  </p>
                </div>
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
              <button className="w-full p-4 bg-surface-soft rounded-xl hover:bg-surface-hover transition-colors text-left flex items-center justify-between">
                <div>
                  <p className="text-[var(--text-primary)] font-medium">Payment Methods</p>
                  <p className="text-[var(--text-primary)] text-xs">
                    Manage your payment options
                  </p>
                </div>
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
              <button className="w-full p-4 bg-surface-soft rounded-xl hover:bg-surface-hover transition-colors text-left flex items-center justify-between">
                <div>
                  <p className="text-[var(--text-primary)] font-medium">Linked Accounts</p>
                  <p className="text-[var(--text-primary)] text-xs">Connect external accounts</p>
                </div>
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
            </div>
          </Card>

          {/* Preferences */}
          <Card>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 card-gradient rounded-full flex items-center justify-center">
                <SettingsIcon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Preferences</h3>
            </div>
            <div className="space-y-3">
              <button className="w-full p-4 bg-surface-soft rounded-xl hover:bg-surface-hover transition-colors text-left flex items-center justify-between">
                <div>
                  <p className="text-[var(--text-primary)] font-medium">Language</p>
                  <p className="text-[var(--text-primary)] text-xs">English (US)</p>
                </div>
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
              <button className="w-full p-4 bg-surface-soft rounded-xl hover:bg-surface-hover transition-colors text-left flex items-center justify-between">
                <div>
                  <p className="text-[var(--text-primary)] font-medium">Currency</p>
                  <p className="text-[var(--text-primary)] text-xs">USD ($)</p>
                </div>
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
              <button className="w-full p-4 bg-surface-soft rounded-xl hover:bg-surface-hover transition-colors text-left flex items-center justify-between">
                <div>
                  <p className="text-[var(--text-primary)] font-medium">Time Zone</p>
                  <p className="text-[var(--text-primary)] text-xs">UTC-5 (Eastern Time)</p>
                </div>
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
            </div>
          </Card>

          {/* Support */}
          <Card>
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
              Support & Legal
            </h3>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/help-center")}
                className="w-full p-4 bg-surface-soft rounded-xl hover:bg-surface-hover transition-colors text-left flex items-center justify-between"
              >
                <p className="text-[var(--text-primary)] font-medium">Help Center</p>
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
              <button
                onClick={() => navigate("/terms-of-service")}
                className="w-full p-4 bg-surface-soft rounded-xl hover:bg-surface-hover transition-colors text-left flex items-center justify-between"
              >
                <p className="text-[var(--text-primary)] font-medium">Terms of Service</p>
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
              <button
                onClick={() => navigate("/privacy-policy")}
                className="w-full p-4 bg-surface-soft rounded-xl hover:bg-surface-hover transition-colors text-left flex items-center justify-between"
              >
                <p className="text-[var(--text-primary)] font-medium">Privacy Policy</p>
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
              <button
                onClick={() => navigate("/about")}
                className="w-full p-4 bg-surface-soft rounded-xl hover:bg-surface-hover transition-colors text-left flex items-center justify-between"
              >
                <p className="text-[var(--text-primary)] font-medium">About</p>
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
            </div>
          </Card>

          {/* Danger Zone */}
          <Card>
            <h3 className="text-lg font-semibold text-red-400 mb-4">
              Danger Zone
            </h3>
            <div className="space-y-3">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => navigate("/login")}
              >
                <LogoutIcon className="w-5 h-5 inline mr-2" />
                Logout
              </Button>
            </div>
          </Card>
        </div>
        <div className="text-center text-[var(--text-primary)] text-sm py-4">
          App Version 1.0.0
        </div>
      </div>
    </div>
  );
};

// Toggle Item Component
const ToggleItem = ({ label, description, checked, onChange }) => {
  return (
    <div className="flex items-center justify-between p-4 bg-surface-soft rounded-xl">
      <div className="flex-1">
        <p className="text-[var(--text-primary)] font-medium">{label}</p>
        <p className="text-[var(--text-primary)] text-xs">{description}</p>
      </div>
      <button
        onClick={onChange}
        className={`relative w-14 h-7 rounded-full transition-colors ${
          checked ? "bg-primary-gradient" : "bg-gray-600"
        }`}
      >
        <div
          className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
            checked ? "transform translate-x-7" : ""
          }`}
        />
      </button>
    </div>
  );
};

export default Settings;
